// components/Audience/QuizView.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase';
import { useAuth } from '../../context/Auth/auth-context';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Quiz, SessionState, LeaderboardEntry } from '../../types/quiz.types';
import { PresenterProfile } from '../../types/presenter.types';
import {
  getQuiz,
  subscribeToSession,
  subscribeToLeaderboard,
  submitAnswer,
  getLeaderboardMeta,
  serverNow,
  POINTS_PER_CORRECT,
} from '../../services/live-session';
import { getPresenterProfile, isFollowing, setFollowing } from '../../services/presenter';
import { track } from '../../services/analytics';
import { toEmbedUrl } from '../../utils/youtube';
import { shareResultCard } from '../../utils/result-card';
import { tileClass, shapeOf, SHAPE, SHAPE_NAME, TILE_FRAME, REVEAL_CORRECT, REVEAL_DIMMED, rankChip } from '../../constants/answer-tiles';

// These stay at module scope ON PURPOSE. Defined inside the component they'd get
// a new identity every render, so React would unmount/remount their whole
// subtree on every state change (timer ticks!) — replaying animations and
// discarding DOM.
const ReconnectBanner = ({ connected }: { connected: boolean }) =>
  connected ? null : (
    <div className="fixed top-0 inset-x-0 z-50 bg-accent text-ink text-center text-sm font-semibold py-1.5">
      Connection lost — reconnecting…
    </div>
  );

const QuestionSplash = ({ show, index, total }: { show: boolean; index: number; total?: number }) =>
  show ? (
    <div className="fixed inset-0 z-40 grid place-items-center bg-ink/95">
      <div className="text-center animate-pop">
        <p className="text-white/70 uppercase tracking-[0.3em] font-semibold">Get ready</p>
        <p className="text-white text-6xl font-black font-display tracking-tight mt-2">
          Question {index + 1}{total ? ` / ${total}` : ''}
        </p>
      </div>
    </div>
  ) : null;

const LeaveLink = ({ light = false, onLeave }: { light?: boolean; onLeave: () => void }) => (
  <button
    onClick={onLeave}
    className={`text-sm underline ${light ? 'text-white/60 hover:text-white' : 'text-slate-400 hover:text-slate-600'}`}
  >
    ✕ Leave game
  </button>
);

const Centered = ({ connected, children }: { connected: boolean; children: React.ReactNode }) => (
  <div className="stage flex items-center justify-center px-4 py-12">
    <ReconnectBanner connected={connected} />
    <div className="card w-full max-w-md p-8 text-center animate-pop">{children}</div>
  </div>
);

export const QuizView = () => {
  const { sessionCode } = useParams<{ sessionCode: string }>();
  const navigate = useNavigate();
  const { auth, userInfo } = useAuth();
  const [session, setSession] = useState<SessionState | null>(null);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [ranks, setRanks] = useState<LeaderboardEntry[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [showVideo, setShowVideo] = useState(true);
  const [connected, setConnected] = useState(true);
  const [splash, setSplash] = useState(false);
  const [totalPlayers, setTotalPlayers] = useState<number | null>(null);
  const [shared, setShared] = useState<'idle' | 'busy' | 'shared' | 'downloaded'>('idle');
  const [submitError, setSubmitError] = useState(false);
  // Ended-screen return hooks: the host's public profile (next show) + whether
  // this player already follows them. Both one-time gets, fetched only at end.
  const [hostProfile, setHostProfile] = useState<PresenterProfile | null>(null);
  const [follows, setFollows] = useState<boolean | null>(null);
  const fetchedQuizId = useRef<string | null>(null);
  const prevIndexRef = useRef<number | null>(null);
  const finishTracked = useRef(false);

  // Local running score, derived entirely on-device from the reveal (zero
  // network). sessionStorage survives a refresh within the same tab; the
  // published leaderboard remains the source of truth.
  const scoreKey = `ql-score-${sessionCode}`;
  const [localScore, setLocalScore] = useState<number>(() => {
    try { return JSON.parse(sessionStorage.getItem(scoreKey) ?? '{}').total ?? 0; }
    catch { return 0; }
  });

  // Which option we answered per question, persisted so a REFRESH can't grant a
  // second answer (the write path would happily overwrite the first one).
  const ansKey = `ql-ans-${sessionCode}`;
  const readAnswerMap = useCallback((): Record<string, number> => {
    try { return JSON.parse(sessionStorage.getItem(ansKey) ?? '{}'); } catch { return {}; }
  }, [ansKey]);

  // STREAM MODE: question lives on the broadcast; phone is a shape-only pad gated
  // by the host's answer window. App mode keeps the on-phone question + timer.
  const isStream = !!session?.youtubeUrl;
  const answersOpen = !!session?.answersOpen;
  const embedUrl = toEmbedUrl(session?.youtubeUrl);
  const revealedIndex = session?.revealedAnswerIndex;
  const revealed = revealedIndex != null;
  // Read-the-question-first (app mode): the host is still reading the question
  // aloud — options and countdown stay hidden until they open answers. Stream
  // mode needs nothing extra here: the pad is shape-only and already gated.
  const delayOptions = !!session?.delayOptions;
  const waitingForOptions = !isStream && delayOptions && !answersOpen && !revealed;

  // Subscribe ONLY to the tiny session delta. No question payload here.
  useEffect(() => {
    if (!sessionCode) return;
    return subscribeToSession(sessionCode, setSession);
  }, [sessionCode]);

  // RTDB's special connection-state path — local SDK state, no fan-out.
  useEffect(() => {
    return onValue(ref(db, '.info/connected'), (snap) => setConnected(!!snap.val()));
  }, []);

  // One-time reads once the game ends: field size for the result framing, plus
  // the host's public profile (the "next stream" hook) and follow state.
  useEffect(() => {
    if (session?.status !== 'ended' || !sessionCode) return;
    getLeaderboardMeta(sessionCode).then((m) => setTotalPlayers(m?.totalPlayers ?? null));
    const hostUid = session.hostUid;
    const myUid = auth?.currentUser?.uid;
    if (!hostUid) return;
    getPresenterProfile(hostUid).then(setHostProfile).catch(() => {});
    if (myUid && myUid !== hostUid) {
      isFollowing(hostUid, myUid).then(setFollows).catch(() => {});
    }
  }, [session?.status, session?.hostUid, sessionCode, auth]);

  // Funnel: one game_finished per session per tab, fired when the end screen
  // first appears.
  useEffect(() => {
    if (session?.status !== 'ended' || finishTracked.current) return;
    finishTracked.current = true;
    track('game_finished', { score: localScore, mode: isStream ? 'stream' : 'app' });
  }, [session?.status, localScore, isStream]);

  // Standings — at the end always; in stream mode also live, so the player sees
  // their rank after each reveal (the on-phone fame moment). This is the one
  // intended fan-out: top-N only, refreshed once per question.
  useEffect(() => {
    if (!sessionCode) return;
    if (session?.status !== 'ended' && !isStream) return;
    return subscribeToLeaderboard(sessionCode, setRanks);
  }, [sessionCode, session?.status, isStream]);

  // Download the full quiz blob exactly once, the first time we learn the quizId.
  useEffect(() => {
    const quizId = session?.quizId;
    if (!quizId || fetchedQuizId.current === quizId) return;
    fetchedQuizId.current = quizId;
    getQuiz(quizId).then(setQuiz);
  }, [session?.quizId]);

  const currentIndex = session?.currentQuestionIndex ?? 0;
  const currentQuestion = quiz?.questions[currentIndex] ?? null;

  // Reset answer state whenever the host advances to a new question (restoring
  // any answer already sent for it — refresh-proof), and flash a short
  // client-side "Question N" splash to buffer the transition.
  useEffect(() => {
    const saved = readAnswerMap()[currentIndex];
    setHasAnswered(saved != null);
    setSelectedOption(saved ?? null);
    setSubmitError(false);
    if (session?.status !== 'in_progress') return;
    if (prevIndexRef.current === null) { prevIndexRef.current = currentIndex; return; }
    if (prevIndexRef.current === currentIndex) return;
    prevIndexRef.current = currentIndex;
    setSplash(true);
    const id = setTimeout(() => setSplash(false), 1200);
    return () => clearTimeout(id);
  }, [currentIndex, session?.status, readAnswerMap]);

  // Bank points locally when the reveal lands and we picked right — once per
  // question (guarded via the sessionStorage map, so a re-reveal can't double).
  useEffect(() => {
    if (revealedIndex == null || !hasAnswered || selectedOption !== revealedIndex) return;
    try {
      const raw = JSON.parse(sessionStorage.getItem(scoreKey) ?? '{}');
      const scored: Record<string, number> = raw.scored ?? {};
      if (scored[currentIndex] != null) return;
      scored[currentIndex] = POINTS_PER_CORRECT;
      const total = (raw.total ?? 0) + POINTS_PER_CORRECT;
      sessionStorage.setItem(scoreKey, JSON.stringify({ scored, total }));
      setLocalScore(total);
    } catch { /* storage unavailable — chip just stays put */ }
  }, [revealedIndex, hasAnswered, selectedOption, currentIndex, scoreKey]);

  // Derive the countdown locally from questionStartTime — consistent across all
  // clients and re-renders, no per-question network read. App mode only: in stream
  // mode there is no fair per-second timer (per-viewer YouTube lag), so answering
  // is gated by the host's window instead.
  // The countdown runs on the SERVER clock (questionStartTime is a server
  // timestamp; serverNow() folds in .info/serverTimeOffset), so every phone
  // agrees on the remaining time regardless of local clock skew.
  useEffect(() => {
    if (isStream || session?.status !== 'in_progress' || !currentQuestion) return;
    // delayOptions: questionStartTime still holds the stale advance timestamp
    // until the host opens — park the clock at full until then (openAnswers
    // rewrites it with a fresh server timestamp, which is when time starts).
    if (delayOptions && !answersOpen) {
      setTimeLeft(currentQuestion.timer);
      return;
    }
    const startTime = session.questionStartTime || serverNow();
    const tick = () => {
      const elapsed = (serverNow() - startTime) / 1000;
      setTimeLeft(Math.max(0, Math.ceil(currentQuestion.timer - elapsed)));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [isStream, session?.status, session?.questionStartTime, currentQuestion, currentIndex, delayOptions, answersOpen]);

  const handleAnswer = async (optionIndex: number) => {
    const uid = auth?.currentUser?.uid;
    // App mode also stops at the reveal: with any residual clock skew the reveal
    // could land while a slow phone still shows 1–2s — never allow answering
    // AFTER the correct answer is on screen.
    const canAnswer = isStream ? answersOpen : timeLeft > 0 && !revealed && !waitingForOptions;
    if (!uid || !sessionCode || hasAnswered || !canAnswer) return;
    setSelectedOption(optionIndex);
    setHasAnswered(true);
    setSubmitError(false);
    try {
      await submitAnswer(sessionCode, uid, currentIndex, optionIndex);
      try {
        const map = readAnswerMap();
        map[currentIndex] = optionIndex;
        sessionStorage.setItem(ansKey, JSON.stringify(map));
      } catch { /* storage unavailable — worst case a refresh re-allows answering */ }
    } catch {
      // The write failed (offline / rules) — don't lie to the player with a
      // "✓ Locked in" they never actually got credit for.
      setHasAnswered(false);
      setSelectedOption(null);
      setSubmitError(true);
    }
  };

  const leave = () => navigate('/');
  const totalQuestions = quiz?.questions.length;

  if (!session) {
    return (
      <Centered connected={connected}>
        <p className="text-slate-500">Loading…</p>
      </Centered>
    );
  }

  if (session.status === 'not_started') {
    return (
      <Centered connected={connected}>
        <div className="flex items-center justify-center gap-2 mb-4">
          {SHAPE.map((shape, i) => (
            <span key={shape} className={`grid place-items-center w-9 h-9 rounded-lg text-white text-lg ${tileClass(i)} ${TILE_FRAME}`}>
              {shape}
            </span>
          ))}
        </div>
        <h2 className="text-2xl font-extrabold font-display tracking-tight">You're in!</h2>
        {quiz ? (
          <>
            <p className="text-lg font-semibold text-ink mt-2">{quiz.title}</p>
            <p className="text-slate-500 mt-1">
              {quiz.questions.length} question{quiz.questions.length === 1 ? '' : 's'} ·{' '}
              {isStream ? 'watch the stream — your phone is the buzzer' : 'plays right here on your phone'}
            </p>
          </>
        ) : (
          <p className="text-slate-500 mt-2">Hang tight — the host will start the game shortly.</p>
        )}
        <div className="mt-4 inline-flex items-center gap-2 text-sm text-slate-400">
          <span className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
          {connected ? 'Connected — waiting for the host to start' : 'Reconnecting…'}
        </div>
        <div className="mt-4"><LeaveLink onLeave={leave} /></div>
      </Centered>
    );
  }

  if (session.status === 'ended') {
    const myUid = auth?.currentUser?.uid;
    const myRankIndex = ranks.findIndex((e) => e.uid === myUid);
    const myEntry = myRankIndex >= 0 ? ranks[myRankIndex] : null;
    const myScore = myEntry?.score ?? localScore;
    // Personal framing built only from data already on the phone + the tiny meta
    // read — never the full score distribution.
    const framing = myEntry
      ? `You finished #${myRankIndex + 1}${totalPlayers ? ` of ${totalPlayers} players` : ''}!`
      : totalPlayers
        ? `You were one of ${totalPlayers} players`
        : null;
    const hostUid = session.hostUid;
    const presenterUrl = hostUid ? `${window.location.origin}/p/${hostUid}` : undefined;
    const handleShare = async () => {
      if (shared === 'busy') return;
      const title = quiz?.title ? `“${quiz.title}”` : 'the live quiz';
      const text = myEntry
        ? `I finished #${myRankIndex + 1}${totalPlayers ? ` of ${totalPlayers}` : ''} with ${myScore.toLocaleString()} pts in ${title} 🏆 Join the next one!${presenterUrl ? ` ${presenterUrl}` : ''}`
        : `I scored ${myScore.toLocaleString()} pts in ${title} 🎯 Join the next one!${presenterUrl ? ` ${presenterUrl}` : ''}`;
      setShared('busy');
      try {
        // The branded IMAGE is the artifact that escapes the app — share it as
        // a file where the OS allows, download it otherwise.
        const outcome = await shareResultCard(
          {
            quizTitle: quiz?.title ?? 'Live quiz',
            playerName: myEntry?.name ?? userInfo?.name ?? 'Player',
            avatar: myEntry?.avatar ?? userInfo?.avatar,
            score: myScore,
            rank: myEntry ? myRankIndex + 1 : undefined,
            totalPlayers: totalPlayers ?? undefined,
            presenterName: hostProfile?.name,
            presenterUrl,
          },
          text
        );
        setShared(outcome);
        track('share_result', { method: outcome });
        setTimeout(() => setShared('idle'), 2500);
      } catch {
        // Canvas/share failed (or the sheet was dismissed) — fall back to text.
        try {
          if (navigator.share) await navigator.share({ text });
          else {
            await navigator.clipboard.writeText(text);
            setShared('shared');
            setTimeout(() => setShared('idle'), 2000);
            return;
          }
          setShared('idle');
        } catch { setShared('idle'); }
      }
    };
    const myUid2 = auth?.currentUser?.uid;
    const toggleFollow = async () => {
      if (!hostUid || !myUid2 || follows === null) return;
      const next = !follows;
      setFollows(next); // optimistic — a single-child write, near-instant
      try {
        await setFollowing(hostUid, myUid2, next);
        if (next) track('follow_presenter', { where: 'ended_screen' });
      } catch { setFollows(!next); }
    };
    const show = hostProfile?.nextShow;
    // Keep an announced show visible for a few hours after start — "live right
    // now" is the best state; long-past announcements just look stale.
    const showIsCurrent = Boolean(show && show.scheduledAt > Date.now() - 4 * 60 * 60 * 1000);
    return (
      <div className="stage px-4 py-12">
        <ReconnectBanner connected={connected} />
        <div className="card max-w-lg mx-auto p-8 animate-pop">
          <h2 className="text-3xl font-extrabold font-display tracking-tight text-center">Final standings</h2>
          <p className="text-center text-slate-500 mb-6">Thanks for playing!</p>
          {(myScore > 0 || myEntry) && (
            <div className="rounded-2xl p-5 mb-6 text-center text-white bg-ink border-2 border-accent shadow-hard-lg">
              <p className="text-sm uppercase tracking-widest text-white/70 font-semibold">Your result</p>
              <p className="text-4xl font-black font-display tracking-tight text-accent mt-1 tabular-nums">{myScore.toLocaleString()} pts</p>
              {framing && <p className="mt-1 font-semibold text-white/90">{framing}</p>}
              <button onClick={handleShare} disabled={shared === 'busy'} className="btn-ghost text-sm mt-3">
                {shared === 'busy'
                  ? 'Making your card…'
                  : shared === 'shared'
                    ? '✓ Shared'
                    : shared === 'downloaded'
                      ? '✓ Card saved — post it!'
                      : 'Share result card'}
              </button>
            </div>
          )}

          {/* Return hooks: the host's next announced show + a follow toggle.
              Data comes from two one-time gets at end — zero standing cost. */}
          {hostUid && (hostProfile || follows !== null) && (
            <div className="rounded-2xl border-2 border-slate-200 p-4 mb-6 text-center">
              {show && showIsCurrent ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                    {hostProfile?.name ? `${hostProfile.name}'s next live quiz` : 'Next live quiz'}
                  </p>
                  <p className="font-display font-extrabold text-lg mt-1">{show.title}</p>
                  <p className="text-slate-500 text-sm">
                    {new Date(show.scheduledAt).toLocaleString(undefined, {
                      weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit',
                    })}
                  </p>
                </>
              ) : (
                <p className="text-sm text-slate-500">
                  {hostProfile?.name
                    ? `Don't miss ${hostProfile.name}'s next live quiz`
                    : "Don't miss the next live quiz"}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-2 mt-3 justify-center">
                {follows !== null && (
                  <button onClick={toggleFollow} className={follows ? 'btn-ghost !text-ink !bg-ink/10' : 'btn-primary'}>
                    {follows ? '✓ Following' : '🔔 Follow this host'}
                  </button>
                )}
                <Link to={`/p/${hostUid}`} className="btn-ghost !text-ink !bg-ink/10 hover:!bg-ink/15 text-center">
                  {hostProfile?.name ? `${hostProfile.name}'s page →` : "Host's page →"}
                </Link>
              </div>
            </div>
          )}
          {ranks.length === 0 ? (
            <p className="text-center text-slate-400">Scores are being tallied…</p>
          ) : (
            <ol className="space-y-2">
              {ranks.map((e, i) => {
                const me = e.uid === myUid;
                return (
                  <li key={e.uid} className={`flex items-center gap-3 p-3 rounded-xl ${me ? 'bg-accent/25 ring-1 ring-accent' : i < 3 ? 'bg-accent/15' : 'bg-slate-50'}`}>
                    <span className={rankChip(i)}>{i + 1}</span>
                    <span className="flex-1 font-medium truncate">{e.avatar ? `${e.avatar} ` : ''}{e.name}{me ? ' (you)' : ''}</span>
                    <span className="font-display font-bold text-ink">{e.score}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <Centered connected={connected}>
        <p className="font-semibold text-slate-700">Getting the next question ready…</p>
      </Centered>
    );
  }

  // STREAM MODE: embedded video (toggle-able) + shape-only answer pad. No question
  // or option text on the phone — the player reads it off the broadcast, which is
  // the whole point (nothing here can leak ahead of the delayed video).
  if (isStream) {
    const myUid = auth?.currentUser?.uid;
    return (
      <div className="stage px-4 py-5">
        <ReconnectBanner connected={connected} />
        <QuestionSplash show={splash} index={currentIndex} total={totalQuestions} />
        <div className="max-w-xl mx-auto">
          <div className="flex items-center justify-between text-white/90 mb-3">
            <span className="font-semibold">Question {currentIndex + 1}{quiz ? ` / ${quiz.questions.length}` : ''}</span>
            <div className="flex items-center gap-2">
              {localScore > 0 && (
                <span className="px-3 py-1 rounded-full bg-white/15 text-white font-display font-bold text-sm tabular-nums">
                  {localScore.toLocaleString()} pts
                </span>
              )}
              {answersOpen ? (
                <span className="chip bg-tile-green text-white animate-pulse">ON AIR</span>
              ) : revealed ? (
                <span className="chip bg-accent text-ink">REVEALED</span>
              ) : (
                <span className="chip bg-ink-soft text-white/80 ring-1 ring-white/20">LOCKED</span>
              )}
            </div>
          </div>

          {/* Host shouted YOU out on stream — the phone joins the moment.
              Rides the session delta already subscribed; cleared on advance. */}
          {session.shoutout?.uid === myUid && (
            <p className="text-center font-display font-bold text-ink bg-accent rounded-xl py-2.5 px-4 mb-3 shadow-hard animate-pop">
              📣 You just got a shout-out on stream!
            </p>
          )}

          {showVideo && (embedUrl ? (
            <div className="relative w-full rounded-2xl overflow-hidden mb-3 bg-black" style={{ aspectRatio: '16 / 9' }}>
              <iframe
                src={embedUrl}
                title="Live stream"
                className="absolute inset-0 w-full h-full"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <a href={session?.youtubeUrl} target="_blank" rel="noreferrer" className="block text-center card p-4 mb-3 text-ink font-semibold">
              ▶ Open the live stream ↗
            </a>
          ))}

          <button onClick={() => setShowVideo((v) => !v)} className="text-white/70 text-sm mb-4 underline">
            {showVideo ? 'Hide video (I’m watching on another screen)' : 'Show video here'}
          </button>

          {revealed ? (
            hasAnswered ? (
              <p className={`text-center font-display font-bold mb-2 py-2 px-4 rounded-xl border-2 shadow-hard animate-pop ${selectedOption === revealedIndex ? 'bg-tile-green border-tile-green-deep text-white' : 'bg-tile-red border-tile-red-deep text-white'}`}>
                {selectedOption === revealedIndex
                  ? `Correct! +${POINTS_PER_CORRECT.toLocaleString()}`
                  : `✗ The answer was ${shapeOf(revealedIndex!)}`}
              </p>
            ) : (
              <p className="text-center font-bold text-white mb-2 animate-pop">
                The answer was {shapeOf(revealedIndex!)}
              </p>
            )
          ) : (
            <p className="text-center text-white font-medium mb-2">
              {hasAnswered ? '✓ Locked in — sit tight!' : answersOpen ? 'Tap the shape that matches your answer' : 'Watch the stream — answers open soon'}
            </p>
          )}
          {submitError && (
            <p className="text-center font-semibold text-ink bg-accent rounded-xl py-2 px-4 mb-2 animate-pop" role="alert">
              Couldn't send your answer — tap again
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            {currentQuestion.options.map((_, optionIndex) => {
              const isCorrect = revealed && optionIndex === revealedIndex;
              const isMyWrongPick = revealed && hasAnswered && selectedOption === optionIndex && !isCorrect;
              const state = revealed
                ? isCorrect
                  ? REVEAL_CORRECT
                  : isMyWrongPick
                    ? 'opacity-70 ring-4 ring-rose-300'
                    : REVEAL_DIMMED
                : hasAnswered && selectedOption !== optionIndex
                  ? 'opacity-30'
                  : answersOpen && !hasAnswered
                    ? 'hover:brightness-110 active:translate-y-1 active:shadow-none'
                    : 'opacity-70';
              return (
                <button
                  key={optionIndex}
                  onClick={() => handleAnswer(optionIndex)}
                  disabled={hasAnswered || !answersOpen}
                  aria-label={`Answer ${SHAPE_NAME[optionIndex % SHAPE_NAME.length]}`}
                  className={`aspect-square grid place-items-center rounded-2xl text-white text-6xl
                    ${tileClass(optionIndex)} ${TILE_FRAME}
                    ${state}
                    ${!revealed && selectedOption === optionIndex ? 'ring-4 ring-accent scale-[1.02]' : ''}
                    transition disabled:cursor-default`}
                >
                  {shapeOf(optionIndex)}
                </button>
              );
            })}
          </div>

          {ranks.length > 0 && (
            <div className="card p-4 mt-5">
              <h3 className="font-bold mb-2 text-xs uppercase tracking-widest text-slate-400">Live standings</h3>
              <ol className="space-y-1.5">
                {ranks.slice(0, 5).map((e, i) => {
                  const me = e.uid === myUid;
                  return (
                    <li key={e.uid} className={`flex items-center gap-3 p-2 rounded-lg ${me ? 'bg-accent/25 ring-1 ring-accent' : ''}`}>
                      <span className={rankChip(i)}>{i + 1}</span>
                      <span className="flex-1 font-medium truncate">{e.avatar ? `${e.avatar} ` : ''}{e.name}{me ? ' (you)' : ''}</span>
                      <span className="font-display font-bold text-ink tabular-nums">{e.score}</span>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          <div className="text-center mt-5"><LeaveLink light onLeave={leave} /></div>
        </div>
      </div>
    );
  }

  const danger = timeLeft <= 5 && !revealed;
  return (
    <div className="stage px-4 py-6">
      <ReconnectBanner connected={connected} />
      <QuestionSplash show={splash} index={currentIndex} total={totalQuestions} />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between text-white/90 mb-3">
          <span className="font-semibold">Question {currentIndex + 1}{quiz ? ` / ${quiz.questions.length}` : ''}</span>
          <div className="flex items-center gap-2">
            {localScore > 0 && (
              <span className="px-3 py-1 rounded-full bg-white/15 text-white font-display font-bold text-sm tabular-nums">
                {localScore.toLocaleString()} pts
              </span>
            )}
            {waitingForOptions ? (
              <span className="chip bg-white/15 text-white ring-1 ring-white/25">LISTEN UP</span>
            ) : (
              <span className={`grid place-items-center w-12 h-12 rounded-full text-xl font-extrabold font-display ${danger ? 'bg-tile-red text-white animate-pulse' : 'bg-ink text-accent ring-2 ring-accent'}`}>
                {timeLeft}
              </span>
            )}
          </div>
        </div>

        <div className="card p-6 sm:p-8 mb-5 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold font-display tracking-tight">{currentQuestion.questionText}</h2>
          {currentQuestion.questionImage && (
            <img src={currentQuestion.questionImage} alt="" className="mt-4 max-h-56 mx-auto rounded-xl object-contain" />
          )}
          {toEmbedUrl(currentQuestion.questionVideo) && (
            <div className="relative w-full max-w-xl mx-auto rounded-xl overflow-hidden mt-4 bg-black" style={{ aspectRatio: '16 / 9' }}>
              <iframe
                src={toEmbedUrl(currentQuestion.questionVideo)!}
                title="Question video"
                className="absolute inset-0 w-full h-full"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>

        {waitingForOptions ? (
          <div className="card p-8 text-center">
            <div className="flex items-center justify-center gap-3">
              {currentQuestion.options.map((_, i) => (
                <span key={i} className={`grid place-items-center w-11 h-11 rounded-xl text-white text-xl ${tileClass(i)} ${TILE_FRAME} opacity-60`}>
                  {shapeOf(i)}
                </span>
              ))}
            </div>
            <p className="mt-4 font-semibold text-ink">
              The presenter is reading the question…
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Options appear when answering opens — then the clock starts.
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {currentQuestion.options.map((optionText, optionIndex) => {
            const isCorrect = revealed && optionIndex === revealedIndex;
            const isMyWrongPick = revealed && hasAnswered && selectedOption === optionIndex && !isCorrect;
            const state = revealed
              ? isCorrect
                ? REVEAL_CORRECT
                : isMyWrongPick
                  ? 'opacity-70 ring-4 ring-rose-300'
                  : REVEAL_DIMMED
              : hasAnswered && selectedOption !== optionIndex
                ? 'opacity-40'
                : 'hover:brightness-110 active:translate-y-1 active:shadow-none';
            return (
              <button
                key={optionIndex}
                onClick={() => handleAnswer(optionIndex)}
                disabled={hasAnswered || timeLeft <= 0 || revealed}
                className={`flex items-center gap-3 p-5 rounded-2xl text-left text-white font-semibold text-lg
                  ${tileClass(optionIndex)} ${TILE_FRAME}
                  ${state}
                  ${!revealed && selectedOption === optionIndex ? 'ring-4 ring-accent scale-[1.02]' : ''}
                  transition disabled:cursor-default`}
              >
                <span className="text-2xl leading-none opacity-90">{shapeOf(optionIndex)}</span>
                <span className="flex-1">{optionText}</span>
                {isCorrect && <span className="text-2xl">✓</span>}
              </button>
            );
          })}
        </div>
        )}

        {revealed ? (
          hasAnswered ? (
            <p className={`mt-5 text-center font-display font-bold py-2 px-4 rounded-xl border-2 shadow-hard animate-pop ${selectedOption === revealedIndex ? 'bg-tile-green border-tile-green-deep text-white' : 'bg-tile-red border-tile-red-deep text-white'}`}>
              {selectedOption === revealedIndex
                ? `Correct! +${POINTS_PER_CORRECT.toLocaleString()}`
                : `✗ The answer was “${currentQuestion.options[revealedIndex!] ?? shapeOf(revealedIndex!)}”`}
            </p>
          ) : (
            <p className="mt-5 text-center font-bold text-white animate-pop">
              The answer was “{currentQuestion.options[revealedIndex!] ?? shapeOf(revealedIndex!)}”
            </p>
          )
        ) : hasAnswered ? (
          <p className="mt-5 text-center text-white font-medium animate-pop">
            ✓ Answer locked in — sit tight for the results.
          </p>
        ) : timeLeft <= 0 ? (
          <p className="mt-5 text-center text-white/90 font-medium">Time's up!</p>
        ) : null}
        {submitError && (
          <p className="mt-3 text-center font-semibold text-ink bg-accent rounded-xl py-2 px-4 animate-pop" role="alert">
            Couldn't send your answer — tap again
          </p>
        )}

        <div className="text-center mt-5"><LeaveLink light onLeave={leave} /></div>
      </div>
    </div>
  );
};
